import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../context/AuthContext';
import { FaUser, FaEnvelope, FaLock, FaEye, FaEyeSlash, FaGlobe } from 'react-icons/fa';
import LoadingSpinner from '../common/LoadingSpinner';
import './Auth.css';

const Register = () => {
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [refCode, setRefCode] = useState('');
  const [extractedUsername, setExtractedUsername] = useState('');

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      currency: 'NGN',
      terms: false,
    }
  });
  const password = watch('password');
  const email = watch('email');

  // Extract username from email
  useEffect(() => {
    if (email && email.includes('@')) {
      const username = email.split('@')[0];
      // Remove special characters and spaces, keep only letters, numbers, and underscores
      const cleanUsername = username.replace(/[^a-zA-Z0-9_]/g, '');
      setExtractedUsername(cleanUsername);
      setValue('username', cleanUsername);
    } else {
      setExtractedUsername('');
    }
  }, [email, setValue]);

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setRefCode(ref);
    }
  }, [searchParams]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    
    // Generate username from email
    const username = data.email.split('@')[0].replace(/[^a-zA-Z0-9_]/g, '');
    
    // If username is empty, use a fallback
    const finalUsername = username || `user_${Date.now().toString(36)}`;
    
    const registerData = {
      username: finalUsername,
      email: data.email,
      password: data.password,
      display_name: finalUsername,
      currency: data.currency || 'NGN',
      ref_code: refCode,
    };
    
    const result = await registerUser(registerData);
    setIsLoading(false);
    
    if (result.success) {
      navigate('/login', { 
        state: { 
          message: 'Registration successful! Please login.',
          email: data.email 
        } 
      });
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="auth-logo">
               <img src='/logo.png' style={{width: 30, height: 30}}/>
              <h1>XPunters</h1>
            </div>
            <p className="auth-subtitle">Create your account and start betting</p>
            {refCode && (
              <div className="referral-badge">
                🎁 Referral code: <strong>{refCode}</strong>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
            {/* Email */}
            <div className="form-group">
              <label>Email Address *</label>
              <div className="input-wrapper">
                
                <input
                  type="email"
                  placeholder="Enter your email address"
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: { 
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Please enter a valid email'
                    }
                  })}
                  className={errors.email ? 'input-error' : ''}
                />
              </div>
              {errors.email && (
                <span className="error-message">{errors.email.message}</span>
              )}
              {email && email.includes('@') && extractedUsername && (
                <div className="username-preview">
                  <FaUser className="preview-icon" />
                  <span>Username: <strong>{extractedUsername}</strong></span>
                </div>
              )}
            </div>

            {/* Password */}
            <div className="form-group">
              <label>Password *</label>
              <div className="input-wrapper">
               
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password (min. 8 characters)"
                  {...register('password', { 
                    required: 'Password is required',
                    minLength: { value: 8, message: 'Password must be at least 8 characters' }
                  })}
                  className={errors.password ? 'input-error' : ''}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {errors.password && (
                <span className="error-message">{errors.password.message}</span>
              )}
              <div className="password-hint">
                <span className={password?.length >= 8 ? 'valid' : ''}>
                  {password?.length >= 8 ? '✅' : '○'} At least 8 characters
                </span>
                <span className={/[A-Z]/.test(password || '') ? 'valid' : ''}>
                  {/[A-Z]/.test(password || '') ? '✅' : '○'} Uppercase letter
                </span>
                <span className={/[0-9]/.test(password || '') ? 'valid' : ''}>
                  {/[0-9]/.test(password || '') ? '✅' : '○'} Number
                </span>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="form-group">
              <label>Confirm Password *</label>
              <div className="input-wrapper">
                
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  {...register('confirmPassword', { 
                    required: 'Please confirm your password',
                    validate: value => value === password || 'Passwords do not match'
                  })}
                  className={errors.confirmPassword ? 'input-error' : ''}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {errors.confirmPassword && (
                <span className="error-message">{errors.confirmPassword.message}</span>
              )}
            </div>

            {/* Currency */}
            <div className="form-group">
              <label>Currency</label>
              <div className="input-wrapper">
                
                <select {...register('currency')} className="form-select">
                  <option value="NGN">₦ NGN - Nigerian Naira</option>
                  <option value="GHS">₵ GHS - Ghanaian Cedi</option>
                  <option value="KES">KSh KES - Kenyan Shilling</option>
                  <option value="ZAR">R ZAR - South African Rand</option>
                  <option value="USD">$ USD - US Dollar</option>
                  <option value="EUR">€ EUR - Euro</option>
                  <option value="GBP">£ GBP - British Pound</option>
                </select>
              </div>
            </div>

            {/* Terms */}
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  {...register('terms', { required: 'You must accept the terms' })}
                />
                <span>I agree to the <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link></span>
              </label>
              {errors.terms && (
                <span className="error-message">{errors.terms.message}</span>
              )}
            </div>

            <button type="submit" className="auth-button" disabled={isLoading}>
              {isLoading ? <LoadingSpinner size="small" /> : 'Create Account'}
            </button>

            <div className="auth-divider">
              <span>or</span>
            </div>

            <p className="auth-footer">
              Already have an account? <Link to="/login">Login</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;